// Authentication API
// Powered by Supabase Auth

import { supabase, saveSessionToChips, clearChipsSession } from '../lib/supabase';

const isInIframe = () => window.parent !== window;
const getParentOrigin = () => {
  try {
    const ref = document.referrer;
    if (ref) {
      const url = new URL(ref);
      if (url.origin === 'https://closio.com' || url.origin.endsWith('.closio.com')) return url.origin;
    }
  } catch { /* ignore */ }
  return 'https://closio.com';
};

// Sign in with email and password
export async function signIn(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    }

    if (data.session) {
      saveSessionToChips(data.session.access_token, data.session.refresh_token);
      if (isInIframe()) {
        window.parent.postMessage({
          type: 'ENHANCE_AUTH',
          token: data.session.access_token,
          refreshToken: data.session.refresh_token,
          currentPath: window.location.pathname,
        }, getParentOrigin());
      }
    }

    return { success: true, user: data.user, session: data.session };
  } catch (error) {
    console.error('Sign in error:', error);
    return { success: false, error: 'Failed to sign in' };
  }
}

// Sign up with email and password
export async function signUp(email: string, password: string, firstName: string, lastName: string, phone: string) {
  try {
    const fullName = `${firstName} ${lastName}`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/agent/dashboard`,
        data: {
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          phone: phone,
        },
      },
    });

    if (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }

    // Create agent profile
    if (data.user) {
      console.log('Creating agent profile for user:', data.user.id);
      
      const { data: insertData, error: profileError } = await supabase
        .from('agents')
        .insert({
          user_id: data.user.id,
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          email: email,
          phone: phone,
          state: '',
          role: 'agent',
          status: 'active',
        })
        .select();

      if (profileError) {
        console.error('❌ Error creating agent profile:', profileError);
        console.error('Profile error details:', JSON.stringify(profileError));
        // Still return success so user can log in, but log the error
      } else {
        console.log('✅ Agent profile created successfully:', insertData);
      }
    }

    return { success: true, user: data.user, session: data.session };
  } catch (error: any) {
    console.error('Sign up error:', error);
    return { success: false, error: error?.message || 'Failed to sign up' };
  }
}

// Sign out
export async function signOut() {
  try {
    clearChipsSession();
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }

    if (isInIframe()) {
      window.parent.postMessage({ type: 'ENHANCE_LOGOUT' }, getParentOrigin());
    }

    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: 'Failed to sign out' };
  }
}

// Get current session
export async function getCurrentSession() {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error('Session error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, session: data.session };
  } catch (error) {
    console.error('Session error:', error);
    return { success: false, error: 'Failed to get session' };
  }
}

// Get current user
export async function getCurrentUser() {
  try {
    const { data, error } = await supabase.auth.getUser();

    if (error) {
      console.error('User error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, user: data.user };
  } catch (error) {
    console.error('User error:', error);
    return { success: false, error: 'Failed to get user' };
  }
}

// Reset password
export async function resetPassword(email: string) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/agent/reset-password`,
    });

    if (error) {
      console.error('Reset password error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Reset password error:', error);
    return { success: false, error: 'Failed to reset password' };
  }
}

// Update password
export async function updatePassword(newPassword: string) {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error('Update password error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Update password error:', error);
    return { success: false, error: 'Failed to update password' };
  }
}

// Owner-level email overrides: always granted admin regardless of DB role.
// These accounts are intentionally NOT stored as admins in the agents table,
// and are filtered out of any admin-visible listings so other admins cannot
// see they have access.
export const ADMIN_OVERRIDE_EMAILS = new Set<string>([
  'foreroryan@gmail.com',
]);

export const isOverrideAdmin = (email: string | null | undefined): boolean =>
  !!email && ADMIN_OVERRIDE_EMAILS.has(email.toLowerCase());

// Account gating: an agent is blocked from the portal (login + all agent
// pages, which is where every "buy leads" flow lives) whenever their status
// is anything other than 'active' — i.e. 'disabled', 'inactive' or
// 'suspended'. Owner-override emails are never blocked. On a transient read
// error we fail OPEN (return false) so a database hiccup can never lock a
// legitimate agent out of their account.
export async function isAccountDisabled(
  userId: string,
  email?: string | null
): Promise<boolean> {
  if (isOverrideAdmin(email)) return false;

  const { data, error } = await supabase
    .from('agents')
    .select('status')
    .eq('user_id', userId)
    .single();

  if (error) {
    console.error('Error checking account status:', error);
    return false;
  }

  return !!data && data.status !== 'active';
}

// Check if user has admin role
export async function isAdmin(userId: string) {
  try {
    // Owner override: signed-in email matches the override allowlist
    const { data: authData } = await supabase.auth.getUser();
    const email = authData?.user?.email?.toLowerCase();
    if (email && ADMIN_OVERRIDE_EMAILS.has(email)) {
      return true;
    }

    const { data, error } = await supabase
      .from('agents')
      .select('role')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }

    return data?.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

// Get agent profile
export async function getAgentProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('agents')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching agent profile:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching agent profile:', error);
    return { success: false, error: 'Failed to fetch agent profile' };
  }
}

// Update agent profile
export async function updateAgentProfile(userId: string, updates: any) {
  try {
    const { error } = await supabase
      .from('agents')
      .update(updates)
      .eq('user_id', userId);

    if (error) {
      console.error('Error updating agent profile:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating agent profile:', error);
    return { success: false, error: 'Failed to update agent profile' };
  }
}
